import { ReactNode } from "react";
import { ArrowRight, Brackets, Pencil, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { CheckResult } from "@/generated";
import { Button } from "@/components/shadcn/button";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import {
  CheckDraft,
  checkName,
  checkTitle,
  kindOf,
  summarize,
} from "./checkCatalog";
import { CheckIcon } from "./outcomes";

export type CheckComparison = {
  baselineLabel: string;
  candidateLabel: string;
  baseline: CheckResult | undefined;
};

export function CheckRow({
  check,
  result,
  compare,
  action,
  onEdit,
  onRemove,
}: {
  check: CheckDraft;
  result?: CheckResult;
  compare?: CheckComparison;
  action?: ReactNode;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const t = useTranslations("agentTests");
  const kind = kindOf(check.type);
  const Icon = kind?.icon ?? Brackets;
  const title = checkTitle(t, check);
  const summary = summarize(t, check.type, check.expected);
  return (
    <div
      data-testid="test-check"
      className="border rounded-lg px-4 py-3 flex items-start gap-3 shadow-xs"
    >
      <div className="flex items-center justify-center size-8 rounded-md border bg-muted shrink-0">
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-xs text-muted-foreground whitespace-pre-line line-clamp-3">
          {summary !== title ? summary : kind ? checkName(t, kind) : null}
        </p>
        {compare && (
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <VersionResult
              testId="baseline-result"
              label={compare.baselineLabel}
              result={compare.baseline}
            />
            <ArrowRight className="size-3" />
            <VersionResult
              testId="candidate-result"
              label={compare.candidateLabel}
              result={result}
            />
          </div>
        )}
        {result?.reason && (
          <p className="text-xs mt-1 line-clamp-3">{result.reason}</p>
        )}
        {action && <div className="mt-1 -ml-2">{action}</div>}
      </div>
      {!compare && result && <CheckIcon check={result} />}
      {kind && (
        <Button
          type="button"
          aria-label={t("checks.row.edit")}
          size="iconsm"
          variant="ghost"
          onClick={onEdit}
        >
          <Pencil />
        </Button>
      )}
      <Button
        type="button"
        aria-label={t("checks.row.remove")}
        size="iconsm"
        variant="ghostDestructive"
        onClick={onRemove}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

function VersionResult({
  testId,
  label,
  result,
}: {
  testId: string;
  label: string;
  result: CheckResult | undefined;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <NoxusTooltip content={label} asChild>
        <span className="max-w-32 truncate">{label}</span>
      </NoxusTooltip>
      <span data-testid={testId} className="inline-flex">
        {result ? <CheckIcon check={result} /> : "-"}
      </span>
    </span>
  );
}
